import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EVIDENCE_TYPES, hasRoleAtLeast, type EvidenceType } from '@raro/domain';
import { Breadcrumbs, Button, EmptyState, Field, Label, Notice, Select, StatusDot, Tag, Textarea } from '@raro/ui';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { EvidenceCard } from '../components/EvidenceCard';
import { SourceBriefCard } from '../components/SourceBriefCard';
import { SuggestionCard } from '../components/Suggestions';
import { api, API_URL } from '../lib/api';
import { useAuth } from '../lib/auth';
import { EVIDENCE_TYPE_LABELS, SOURCE_STATUS_LABELS, SOURCE_TYPE_LABELS, formatDate, formatNumber, locationLabel, sourceIsPending } from '../lib/format';
import type { Chunk, Me, ProjectDetail, SourceDetail, Suggestion } from '../lib/types';
import { SourceBrand } from '../components/SourceBrand';
import { SourcePlace } from '../components/SourcePlace';

export function FontePage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const { me } = useAuth();
  const [showAll, setShowAll] = useState(false);
  const source = useQuery({
    queryKey: ['source', id],
    queryFn: () => api.get<SourceDetail>(`/sources/${id}`),
    refetchInterval: (q) => (q.state.data && sourceIsPending(q.state.data.status) ? 2500 : false),
  });
  const project = useQuery({
    queryKey: ['project', source.data?.projectId],
    queryFn: () => api.get<ProjectDetail>(`/projects/${source.data!.projectId}`),
    enabled: Boolean(source.data?.projectId),
  });
  const candidates = useQuery({
    queryKey: ['suggestions', source.data?.projectId, 'source', id],
    queryFn: () => api.get<Suggestion[]>(`/projects/${source.data!.projectId}/suggestions?status=pending&sourceId=${id}`),
    enabled: Boolean(source.data?.projectId),
  });
  const [marking, setMarking] = useState<{ chunk: Chunk; content: string; type: EvidenceType; note: string } | null>(null);

  const reprocess = useMutation({
    mutationFn: () => api.post(`/sources/${id}/process`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['source', id] }),
  });
  const createEvidence = useMutation({
    mutationFn: (m: NonNullable<typeof marking>) =>
      api.post(`/sources/${id}/evidence`, { chunkId: m.chunk.id, content: m.content.trim(), evidenceType: m.type, note: m.note.trim() || undefined }),
    onSuccess: () => {
      setMarking(null);
      void qc.invalidateQueries({ queryKey: ['source', id] });
      void qc.invalidateQueries({ queryKey: ['evidence'] });
    },
  });

  if (source.isPending) return <div className="rr-page rr-faint">carregando…</div>;
  if (source.isError || !source.data) {
    return (
      <div className="rr-page">
        <Notice tone="error">não encontramos esta fonte.</Notice>
      </div>
    );
  }
  const s = source.data;
  const role = roleFor(me, s.organizationId);
  const internal = role !== 'client';
  const canValidate = hasRoleAtLeast(role, 'partner');

  const startMarking = (chunk: Chunk) => {
    const selected = window.getSelection()?.toString().trim();
    setMarking({ chunk, content: selected && selected.length > 10 ? selected : chunk.content, type: 'quote', note: '' });
  };

  return (
    <>
      <div className="rr-topbar">
        <Breadcrumbs
          items={[
            { label: 'Projetos', render: (l) => <Link to="/projetos">{l}</Link> },
            ...(project.data ? [{ label: project.data.organization.name }, { label: project.data.name, render: (l: string) => <Link to={`/projetos/${project.data!.id}`}>{l}</Link> }] : []),
            { label: 'Fonte' },
          ]}
        />
        <div className="rr-row">
          {s.competitors?.map((c) => <Link key={c.id} to={`/clientes/${c.organizationId}`} title="fonte associada a este concorrente"><Tag tone="accent">concorrente · {c.name}</Tag></Link>)}
          <Tag tone="outline">{SOURCE_TYPE_LABELS[s.type]}</Tag>
          <span className="rr-row" style={{ fontSize: 'var(--rr-text-xs)', color: 'var(--rr-text-3)' }}>
            <StatusDot tone={s.status === 'processed' ? 'active' : s.status === 'failed' ? 'down' : 'hollow'} />
            {SOURCE_STATUS_LABELS[s.status]}
          </span>
          {internal && (
            <Button size="sm" onClick={() => reprocess.mutate()} disabled={reprocess.isPending || sourceIsPending(s.status)}>
              reprocessar
            </Button>
          )}
        </div>
      </div>

      <div className="rr-page rr-page--tight rr-stack rr-stack--6">
        <header className="rr-stack rr-stack--3">
          <h1 className="rr-title">{s.title}</h1>
          {s.description && <p className="rr-muted">{s.description}</p>}
          <div className="rr-row rr-row--wrap" style={{ fontSize: 'var(--rr-text-xs)', color: 'var(--rr-text-3)', gap: 'var(--rr-space-4)' }}>
            {internal && !s.projectId && <SourceBrand sourceId={s.id} organizationId={s.organizationId} brandId={(s as { brandId?: string | null }).brandId ?? null} canEdit={internal} />}
            {internal && <SourcePlace sourceId={s.id} organizationId={s.organizationId} placeId={s.place?.id ?? null} pointId={s.point?.id ?? null} canEdit={internal} />}
            {(() => { const fileName = s.file?.originalName ?? (s.metadata as { originalTitle?: string }).originalTitle; return fileName && fileName !== s.title ? <span title="nome do arquivo">{fileName}</span> : null; })()}
            <span>{formatDate(s.createdAt)}</span>
            {s.wordCount ? <span>{formatNumber(s.wordCount)} palavras</span> : null}
            <span>{s.chunks.length} trechos</span>
            {s.chunks.length > 0 && <span>{s.chunks.every((c) => c.hasEmbedding) ? 'vetorizado' : 'sem vetores'}</span>}
            {s.language && <span>{s.language}</span>}
            {s.file && (
              <a href={`${API_URL}${s.file.url}`} target="_blank" rel="noreferrer" style={{ textDecoration: 'underline', textUnderlineOffset: 3 }}>
                abrir original · {(s.file.sizeBytes / 1024).toFixed(0)} KB
              </a>
            )}
            {s.url && (
              <a href={s.url} target="_blank" rel="noreferrer" style={{ textDecoration: 'underline', textUnderlineOffset: 3 }}>
                abrir link
              </a>
            )}
          </div>
          {s.error && <Notice tone={s.status === 'failed' ? 'error' : undefined}>{s.error}</Notice>}
          {(s.metadata as { classification?: { summary: string; relevance: string; meritsDeepAnalysis: boolean; topics: string[] } }).classification && (
            <p className="rr-faint" style={{ fontSize: 'var(--rr-text-sm)' }}>
              <span className="rr-label rr-label--accent">Machine</span> {(s.metadata as { classification: { summary: string } }).classification.summary}{' '}
              <span className="rr-faint">· relevância {(s.metadata as { classification: { relevance: string } }).classification.relevance}{(s.metadata as { classification: { meritsDeepAnalysis: boolean } }).classification.meritsDeepAnalysis ? ' · merece análise profunda' : ''}</span>
            </p>
          )}
        </header>

        {s.brief && (
          <SourceBriefCard
            brief={s.brief}
            frames={s.frames ?? []}
            onJump={(atMs) => {
              // trecho mais próximo daquele minuto; abre a transcrição se estiver recolhida
              setShowAll(true);
              const target = s.chunks.filter((c) => c.location?.startMs !== undefined).reduce<Chunk | null>((best, c) => (!best || Math.abs((c.location.startMs ?? 0) - atMs) < Math.abs((best.location.startMs ?? 0) - atMs) ? c : best), null);
              if (!target) return;
              setTimeout(() => { const el = document.getElementById(`chunk-${target.id}`); el?.scrollIntoView({ behavior: 'smooth', block: 'center' }); el?.classList.add('rr-chunk--flash'); setTimeout(() => el?.classList.remove('rr-chunk--flash'), 1700); }, 50);
            }}
          />
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 360px', gap: 'var(--rr-space-7)', alignItems: 'start' }}>
          <section className="rr-section">
            <div className="rr-section__head">
              <Label>{s.brief ? 'transcrição e leitura completa' : 'Conteúdo'}{s.brief ? ` · ${s.chunks.length} trechos` : ''}</Label>
              {s.brief && s.chunks.length > 0 && <button type="button" className="rr-budget__toggle" onClick={() => setShowAll((v) => !v)}>{showAll ? 'recolher' : 'mostrar tudo'}</button>}
              {internal && s.chunks.length > 0 && (
                <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>
                  selecione um trecho e marque como evidência
                </span>
              )}
            </div>
            {sourceIsPending(s.status) && <EmptyState title="a Machine está lendo…">extração, fragmentação e indexação em andamento.</EmptyState>}
            {s.status === 'processed' && s.chunks.length === 0 && <EmptyState title="nada legível ainda.">{s.error ?? 'a fonte não produziu texto.'}</EmptyState>}
            <div>
              {(s.brief && !showAll ? s.chunks.slice(0, 3) : s.chunks).map((c) => (
                <div key={c.id} className="rr-chunk" id={`chunk-${c.id}`}>
                  <div className="rr-chunk__loc">{locationLabel(c.location) || `#${c.index + 1}`}</div>
                  <div>
                    <div className="rr-chunk__text">{c.content}</div>
                    {internal && (
                      <div className="rr-chunk__actions">
                        <Button size="sm" onMouseDown={(e) => e.preventDefault()} onClick={() => startMarking(c)}>
                          marcar como evidência →
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <aside className="rr-stack rr-stack--5" style={{ position: 'sticky', top: 'var(--rr-space-5)' }}>
            {marking ? (
              <div className="rr-card rr-stack rr-stack--4">
                <Label tone="accent">Nova evidência</Label>
                <Field label="Trecho" htmlFor="ev-content">
                  <Textarea id="ev-content" rows={6} value={marking.content} onChange={(e) => setMarking({ ...marking, content: e.target.value })} />
                </Field>
                <Field label="Tipo" htmlFor="ev-type">
                  <Select id="ev-type" value={marking.type} onChange={(e) => setMarking({ ...marking, type: e.target.value as EvidenceType })}>
                    {EVIDENCE_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {EVIDENCE_TYPE_LABELS[t]}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Nota" htmlFor="ev-note" hint="por que isso importa? o que você está vendo?">
                  <Textarea id="ev-note" rows={2} value={marking.note} onChange={(e) => setMarking({ ...marking, note: e.target.value })} />
                </Field>
                {createEvidence.isError && <Notice tone="error">{(createEvidence.error as Error).message}</Notice>}
                <div className="rr-row">
                  <Button variant="primary" onClick={() => createEvidence.mutate(marking)} disabled={createEvidence.isPending || marking.content.trim().length < 3}>
                    salvar evidência
                  </Button>
                  <Button variant="quiet" onClick={() => setMarking(null)}>
                    cancelar
                  </Button>
                </div>
              </div>
            ) : (
              <>
              {internal && candidates.data && candidates.data.length > 0 && s.projectId && (
                <section className="rr-section">
                  <div className="rr-section__head">
                    <Label tone="accent">a Machine sugere</Label>
                    <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>{candidates.data.length} candidata(s)</span>
                  </div>
                  <div className="rr-stack rr-stack--3">{candidates.data.map((c) => <SuggestionCard key={c.id} s={c} projectId={s.projectId!} compact />)}</div>
                </section>
              )}
              <section className="rr-section">
                <div className="rr-section__head">
                  <Label>Evidências desta fonte</Label>
                  <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>
                    {s.evidence.length}
                  </span>
                </div>
                {s.evidence.length === 0 ? (
                  <p className="rr-faint" style={{ fontSize: 'var(--rr-text-sm)' }}>
                    nenhuma ainda. evidência é o trecho verificável que você escolhe guardar.
                  </p>
                ) : (
                  <div className="rr-stack rr-stack--3">
                    {s.evidence.map((e) => (
                      <EvidenceCard key={e.id} evidence={e} canValidate={canValidate} />
                    ))}
                  </div>
                )}
              </section>
              </>
            )}
          </aside>
        </div>
      </div>
    </>
  );
}

function roleFor(me: Me | null, organizationId: string) {
  if (!me) return 'client' as const;
  const direct = me.memberships.find((m) => m.organizationId === organizationId)?.role;
  if (direct && me.internalRole) return hasRoleAtLeast(direct, me.internalRole) ? direct : me.internalRole;
  return direct ?? me.internalRole ?? ('client' as const);
}
